'use client';

import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import styles from './entityCategoryImage.module.scss';
import { CSSProperties, ReactNode } from 'react';
import { responsiveBreakpointsPx } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { colorBrightness } from '@/packages/color/colorBrightness';
import cn from 'classnames';
import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';

const HALF_LUMINANCE = 255 / 2;
const DEFAULT_BACKGROUND_COLOR = 'var(--base-contrast-accent, #0000eb)';

const {
    imageWrapper,
    imageContainer,
    imageContainerStack,
    imageContainerStackSingle,
    imageContainerSingle,
    imageLength,
    exploreAllImageStack,
    exploreAllImageSmallSized,
    itemContainer,
    itemBackground,
    imageSmall,
    imageDropdown,
} = styles;

export enum Variant {
    collection = 'collection',
    stack = 'stack',
    single = 'single',
}

export interface ImageItem {
    image: ReactNode;
    backgroundColor?: string;
}

export interface EntityCategoryImageProps {
    squareType?: boolean;
    items: ImageItem[];
    variant?: Variant;
    isMultiple?: boolean;
    exploreAllBackgroundColor?: string;
    isWayToListenCard?: boolean;
    isImageSmall?: boolean;
    isInDropdown?: boolean;
}

export function EntityCategoryImage({
    items,
    variant = Variant.collection,
    squareType = false,
    isMultiple = false,
    exploreAllBackgroundColor = '#0000eb', // Brand Blue
    isWayToListenCard = false,
    isImageSmall = false,
    isInDropdown = false,
}: EntityCategoryImageProps) {
    let content = null;

    const borderStyles = {
        ...{ '--border-radius': squareType ? 'var(--border-radius-16)' : 'var(--border-radius-capsule)' },
        ...{ '--border-radius-sm': squareType ? 'var(--rounding-small, 8px)' : 'var(--border-radius-capsule)' },
    } as CSSProperties;

    const viewportIsLargeSized = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.lg}px)`);

    if (variant === Variant.collection) {
        // If there is Ways to Listen Card
        if (isWayToListenCard) {
            content = items.map((item, index) => (
                <StyledContainer
                    key={index}
                    className={cn(imageContainer, itemBackground)}
                    style={{ ...borderStyles, ...backgroundColorProps(item.backgroundColor || 'transparent') }}
                >
                    <div className={itemContainer}> {item.image}</div>
                </StyledContainer>
            ));
            // If there is one or two Images we show just the first Image
        } else if (items.length === 1 || items.length === 2) {
            const [item] = items;
            content = (
                <StyledContainer
                    className={cn(imageContainer, itemBackground)}
                    style={{ ...borderStyles, ...backgroundColorProps(item.backgroundColor) }}
                >
                    {item.image}
                </StyledContainer>
            );
        } else if (items.length === 3) {
            content = items.map((item, index) => (
                <StyledContainer
                    key={index}
                    className={cn(imageContainer, itemBackground)}
                    style={{ ...borderStyles, ...backgroundColorProps(item.backgroundColor) }}
                >
                    {item.image}
                </StyledContainer>
            ));
        }
    } else if (variant === Variant.stack) {
        // If there is one or two Images we show just the first Image
        if ((items.length === 1 || items.length === 2) && !isMultiple) {
            const [item] = items;
            content = (
                <StyledContainer
                    className={cn(imageContainerStackSingle, itemBackground, {
                        [imageSmall]: isImageSmall,
                        [imageDropdown]: isInDropdown,
                    })}
                    style={{ ...borderStyles, ...backgroundColorProps(item.backgroundColor) }}
                >
                    {item.image}
                </StyledContainer>
            );
        } else if (items.length === 3 && !isMultiple) {
            content = items.map((item, index) => (
                <StyledContainer
                    key={index}
                    className={cn(imageContainerStack, itemBackground, {
                        [imageDropdown]: isInDropdown,
                    })}
                    style={{ ...borderStyles, ...backgroundColorProps(item.backgroundColor) }}
                >
                    {item.image}
                </StyledContainer>
            ));
        } else if (items.length > 3 || (items.length === 3 && !viewportIsLargeSized)) {
            const textColor =
                colorBrightness(exploreAllBackgroundColor) > HALF_LUMINANCE ? 'var(--black)' : 'var(--white)';
            const exploreAllColorCustomProps = {
                '--explore-all-text-color': textColor,
            };

            content = (
                <StyledContainer
                    className={cn(imageContainerStack, {
                        [exploreAllImageSmallSized]: items.length === 3 && !viewportIsLargeSized,
                        [exploreAllImageStack]: items.length !== 3 || viewportIsLargeSized,
                        [imageDropdown]: isInDropdown,
                    })}
                    backgroundColor={exploreAllBackgroundColor}
                    style={{ ...borderStyles, ...exploreAllColorCustomProps }}
                >
                    <div className={imageLength}>+{!viewportIsLargeSized ? items.length - 1 : items.length - 3}</div>
                </StyledContainer>
            );
        }
    } else if (variant === Variant.single) {
        const [item] = items;
        content = (
            <StyledContainer
                className={cn(imageContainer, imageContainerSingle, itemBackground)}
                style={{ ...borderStyles, ...backgroundColorProps(item.backgroundColor) }}
            >
                {item.image}
            </StyledContainer>
        );
    }

    return <div className={imageWrapper}>{content}</div>;
}

function backgroundColorProps(color?: string): CSSPropsVars {
    return {
        '--entity-category-image-bg-color': color || DEFAULT_BACKGROUND_COLOR,
    };
}
